import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { accountError } from './AccountDialog';

export const CloudAccountContext = createContext({ runtimeMode: null, openPassword: null });
export const useCloudAccount = () => useContext(CloudAccountContext);

export const CloudAccountGate = ({ children, onLogout }) => {
  const [account, setAccount] = useState(null);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const mode = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pos/mode`);
        if (!active) return;
        if (mode.data.mode === 'local_pos') {
          // Local startup/recovery must not depend on this Cloud account gate.
          setAccount({ runtime_mode: 'local_pos' });
          return;
        }
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/me`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
        if (active) setAccount(data);
      } catch (e) { if (active) setError(accountError(e)); }
    };
    check();
    const interceptor = axios.interceptors.response.use(response => response, e => {
      if (active && (e.response?.data?.code || e.response?.data?.detail?.code) === 'password_change_required') {
        setAccount(old => ({ ...old, runtime_mode: 'cloud', must_change_password: true }));
      }
      if (e.response?.data?.detail?.code === 'admin_view_only') {
        // One clear notice however the calling screen handles its own error.
        toast.error(e.response.data.detail.message, { id: 'admin-view-only' });
      }
      if (e.response?.status === 402 && ['plan_limit', 'plan_feature'].includes(e.response?.data?.detail?.code)) {
        toast.error(e.response.data.detail.message, { id: 'plan-limit', description: 'Цэс → Багц хэсгээс ахиулна.' });
      }
      return Promise.reject(e);
    });
    return () => { active = false; axios.interceptors.response.eject(interceptor); };
  }, []);
  const changed = token => {
    localStorage.setItem('admin_token', token);
    setAccount(old => ({ ...old, must_change_password: false }));
    setShowPassword(false); setGeneration(old => old + 1);
  };
  if (error) return <div className="p-8" data-testid="account-load-error" role="alert">{error}<Button data-testid="account-error-logout" onClick={onLogout} className="ml-3">Гарах</Button></div>;
  if (!account) return <div className="p-8" data-testid="account-loading" role="status">Түр хүлээнэ үү…</div>;
  const cloud = account.runtime_mode === 'cloud';
  const forced = cloud && account.must_change_password === true;
  return <CloudAccountContext.Provider value={{ runtimeMode: account.runtime_mode, openPassword: cloud ? () => setShowPassword(true) : null }}>
    {!forced && <React.Fragment key={generation}>{children}</React.Fragment>}
    {(forced || showPassword) && <ChangePasswordDialog forced={forced} onClose={() => setShowPassword(false)} onChanged={changed} onLogout={onLogout} />}
  </CloudAccountContext.Provider>;
};